import { useMemo, useState, useEffect, useRef } from 'react'
import { useApp } from '@/context/AppContext'
import { useFilteredBreakValues } from '@/hooks/useFilteredBreakValues'
import { ScatterChart, Scatter, XAxis, YAxis, ZAxis, ResponsiveContainer, Cell, ReferenceLine, Label } from 'recharts'
import { getTribeColor, TABLEAU_10, PROVINCE_COLORS, GENDERATION_COLORS, FINANCIAL_COLORS, REGION_DETAILED_COLORS, PARTY_COLORS, ETHNICITY_COLORS, UNION_COLORS, INDUSTRY_COLORS, MEDIA_COLORS, IDENTITY_COLORS } from '@/utils/colors'
import colorsConfig from '@/config/colors.json'
import type { TribeId } from '@/types'

// Short display names for industry sectors (for cleaner scatterplot labels)
const INDUSTRY_SHORT_NAMES: Record<string, string> = {
  'Oil and gas': 'Oil & Gas',
  'Coal': 'Coal',
  'Renewable energy': 'Renewable',
  'Forestry': 'Forestry',
  'Fishing': 'Fishing',
  'Food and agriculture': 'Agriculture',
  'Construction or manufacturing': 'Construction',
  'Transportation, logistics, or storage': 'Transport',
  'Real estate or property management': 'Real Estate',
  'Professional services': 'Prof. Services',
  'Finance and insurance': 'Finance',
  'Retail services': 'Retail',
  'Tourism, hospitality, and recreation': 'Tourism',
  'Arts, culture, and entertainment': 'Arts/Culture',
  'Healthcare': 'Healthcare',
  'Education and training': 'Education',
}

// Short display names for financial situation (for cleaner scatterplot labels)
const FINANCIAL_SHORT_NAMES: Record<string, string> = {
  'Falling behind on monthly expenses, in debt': 'In Debt',
  'Making ends meet, but with no savings': 'No Savings',
  'Getting by, with some savings': 'Some Savings',
  'Financially secure': 'Secure',
  'Well-off': 'Well-off',
}

// Province/territory abbreviations for scatterplot labels
const PROVINCE_SHORT_NAMES: Record<string, string> = {
  'British Columbia': 'BC',
  'Alberta': 'AB',
  'Saskatchewan': 'SK',
  'Manitoba': 'MB',
  'Ontario': 'ON',
  'Quebec': 'QC',
  'New Brunswick': 'NB',
  'Nova Scotia': 'NS',
  'Prince Edward Island': 'PEI',
  'Newfoundland & Labrador': 'NL',
  'Yukon': 'YT',
  'Northwest Territories': 'NWT',
  'Nunavut': 'NU',
}

// Short names for ethnicity labels
const ETHNICITY_SHORT_NAMES: Record<string, string> = {
  'Indigenous': 'Indigenous',
  'White': 'White',
  'Western European': 'W. Euro',
  'British Isles': 'British',
  'East Asian': 'E. Asian',
  'Caucasian': 'Caucasian',
  'South Asian': 'S. Asian',
  'Black': 'Black',
  'Hispanic': 'Hispanic',
  'African': 'African',
  'Mixed': 'Mixed',
  'Jewish': 'Jewish',
  'Eastern European': 'E. Euro',
  'Middle Eastern': 'Mid East',
  'Quebecois': 'Québécois',
  'Canadian': 'Canadian',
  'Other': 'Other',
}

// Short names for identity labels
const IDENTITY_SHORT_NAMES: Record<string, string> = {
  'Indigenous or First Nations': 'Indigenous',
  'New Canadian or Immigrant': 'New Canadian',
  'Temporary foreign worker or student': 'Temp Worker',
  'Wellness lifestyle': 'Wellness',
  'Nature-Lover': 'Nature-Lover',
  'Canadian': 'Canadian',
  'Settler': 'Settler',
  'Hunter': 'Hunter',
  'Angler': 'Angler',
  'Traditional': 'Traditional',
}

// Data point type for scatterplot
interface DataPoint {
  id: string
  name: string
  x: number
  y: number
  size: number
  color: string
  labelName?: string      // Short name for label (e.g., province name for regiondetailed)
  labelColor?: string     // Color for the label text
  showLabel?: boolean     // Whether to show a label for this point (default: true)
  percentage?: number     // Percentage of total for label display
}

// Tribe position data type
interface TribePosition {
  id: TribeId
  name: string
  x: number
  y: number
  size: number
}

// Tribe positions from Tableau PCA analysis (Scatterplot-draft_data.csv)
// X-axis: Trust in systems/institutions - Populist Alienation (left/low) vs Institutional Trust (right/high)
// Y-axis: Relationship to nature - Extraction/Dominance (bottom/low) vs Mutualism/Co-dependence (top/high)
// Size: % of total weight
// Raw data:
// Segment, PCA2*-1, PCA1, % Weight
// The Woke, -0.085, 3.96, 6.16%
// Mainstream Progressives, 0.015, 3.69, 13.32%
// Older Institutionalists, 1.87, 1.61, 9.26%
// Indigenous Identified, -0.54, 0.37, 6.47%
// Struggling Green Populists, -1.86, 0.42, 19.26%
// New Canadian Materialists, 0.50, -1.59, 16.44%
// Community-First Elders, 1.83, -0.90, 9.25%
// Pro-Extraction Populists, -0.38, -5.20, 19.84%

// Use raw UMAP values directly (like Atlantic Canada model)
// X: UMAP2D_1 (Attitude toward governance)
// Y: UMAP2D_2 (Attitude toward nature)
// No normalization - show actual data values for proper data science methodology
const normalizeX = (umap1: number) => umap1  // Pass through raw value
const normalizeY = (umap2: number) => umap2  // Pass through raw value

// Tribe positions from pablo v2 weights (January 2026)
// X = Attitude toward Governance, Y = Attitude toward Nature
// Size = Segment Size (%)
const TRIBE_POSITIONS: TribePosition[] = [
  { id: 'pro-extraction-populists', name: 'Pro-Extraction Populists', x: normalizeX(-3.764), y: normalizeY(-2.765), size: 19.8 },
  { id: 'community-first-elders', name: 'Community-First Elders', x: normalizeX(-1.131), y: normalizeY(-1.542), size: 9.2 },
  { id: 'new-canadian-materialists', name: 'New Canadian Materialists', x: normalizeX(-2.122), y: normalizeY(-1.314), size: 16.4 },
  { id: 'pessimistic-eco-sympathizers', name: 'Struggling Green Populists', x: normalizeX(-1.819), y: normalizeY(0.617), size: 19.3 },
  { id: 'indigenous-identified', name: 'Indigenous Identified', x: normalizeX(-3.187), y: normalizeY(0.530), size: 6.5 },
  { id: 'older-institutionalists', name: 'Older Institutionalists', x: normalizeX(0.310), y: normalizeY(0.024), size: 9.3 },
  { id: 'mainstream-progressives', name: 'Mainstream Progressives', x: normalizeX(0.426), y: normalizeY(1.874), size: 13.3 },
  { id: 'the-woke', name: 'The Woke', x: normalizeX(0.680), y: normalizeY(2.200), size: 6.2 },
]

// Note: Scatterplot positions now come from pre-calculated UMAP data in aggregated.json
// The aggregation script calculates weighted mean UMAP2D_1 (X) and UMAP2D_2 (Y) for each break value

// Custom scatter label renderer for full control over styling
// Label is positioned to the right of the circle, accounting for circle radius
interface CustomLabelProps {
  x?: number
  y?: number
  name?: string
  value?: string
  fill?: string
  // Recharts passes the data point's size as z
  z?: number
  // Custom fields for regiondetailed
  labelName?: string
  labelColor?: string
  showLabel?: boolean
  // For highlighting
  isHighlighted?: boolean
  hasHoveredPoint?: boolean
  // Percentage for display
  percentage?: number
}

// Simple label positioning - always to the right, unless near right edge
function calculateLabelPosition(
  pixelX: number, _pixelY: number, _index: number,
  _allPoints: Array<{ x: number; y: number }>,
  chartWidth: number, _chartHeight: number
): 'right' | 'left' | 'top' | 'bottom' {
  const RIGHT_MARGIN = 150
  const plotRight = chartWidth - RIGHT_MARGIN

  // Only use left if very close to right edge
  if (pixelX > plotRight - 80) {
    return 'left'
  }
  return 'right'
}

// Placeholder for resetPlacedLabels (called elsewhere)
function resetPlacedLabels() {
  // No-op
}

// Store for passing point positions to labels
let _allPointPositions: Array<{ x: number; y: number }> = []
let _chartDimensions = { width: 1000, height: 700 }

function setPointPositions(points: Array<{ x: number; y: number }>, width: number, height: number) {
  _allPointPositions = points
  _chartDimensions = { width, height }
}

function CustomScatterLabel({
  x, y, name, fill = '#1e293b', z, index,
  labelName, labelColor, showLabel, isHighlighted, hasHoveredPoint, percentage
}: CustomLabelProps & { index?: number }) {
  if (x === undefined || y === undefined || !name) return null

  // If showLabel is explicitly false, don't show label
  if (showLabel === false) return null

  // Use labelName if provided (e.g., just province name for regiondetailed)
  // Add percentage in brackets if available
  const baseName = labelName || name
  const displayText = percentage !== undefined
    ? `${baseName} (${percentage.toFixed(0)}%)`
    : baseName

  // Use labelColor if provided, otherwise fall back to fill
  // If there's a hovered point but this isn't it, gray out the label
  let textColor = labelColor || fill
  let opacity = 1
  if (hasHoveredPoint && !isHighlighted) {
    textColor = '#9ca3af' // gray-400
    opacity = 0.5
  }

  // Calculate circle radius from area (z value) - ZAxis range is [150, 800]
  // Recharts uses area for bubble size, so radius = sqrt(area / π)
  const estimatedRadius = z ? Math.sqrt(z / Math.PI) : 10
  const offset = Math.max(estimatedRadius + 8, 18)

  // Calculate optimal position based on nearby points
  const position = calculateLabelPosition(
    x, y, index ?? 0,
    _allPointPositions,
    _chartDimensions.width,
    _chartDimensions.height
  )

  const labelProps = {
    fill: textColor,
    fontSize: 12,
    fontWeight: 500,
    style: { pointerEvents: 'none' as const, opacity },
  }

  switch (position) {
    case 'top':
      return (
        <text x={x} y={y - offset - 4} textAnchor="middle" dominantBaseline="auto" {...labelProps}>
          {displayText}
        </text>
      )
    case 'bottom':
      return (
        <text x={x} y={y + offset + 4} textAnchor="middle" dominantBaseline="hanging" {...labelProps}>
          {displayText}
        </text>
      )
    case 'left':
      return (
        <text x={x - offset} y={y} textAnchor="end" dominantBaseline="middle" {...labelProps}>
          {displayText}
        </text>
      )
    case 'right':
    default:
      return (
        <text x={x + offset} y={y} textAnchor="start" dominantBaseline="middle" {...labelProps}>
          {displayText}
        </text>
      )
  }
}

// Dynamic domain based on actual data range (like Atlantic Canada model)
// Will be calculated from data in useMemo below

export function Scatterplot() {
  const { state } = useApp()
  const { data, filters } = state
  const { selectedBreak } = filters

  // Track hovered point for dynamic info box
  const [hoveredPoint, setHoveredPoint] = useState<DataPoint | TribePosition | null>(null)
  const hoverTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  
  // Always use full 0-100 domain for consistent comparison across breaks
  // (Removed zoom animation - distorts comparisons between breaks)

  // Helper to set hover with timeout clearing
  const handleHoverEnter = (point: DataPoint | TribePosition) => {
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current)
      hoverTimeoutRef.current = null
    }
    setHoveredPoint(point)
  }

  // Helper to clear hover with short delay (allows moving between points)
  const handleHoverLeave = () => {
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current)
    }
    hoverTimeoutRef.current = setTimeout(() => {
      setHoveredPoint(null)
    }, 100) // 100ms delay - fast but allows smooth transition between points
  }

  // Clear hovered point when break changes
  useEffect(() => {
    setHoveredPoint(null)
  }, [selectedBreak])

  // Detect dark mode for label colors
  const [isDarkMode, setIsDarkMode] = useState(false)
  useEffect(() => {
    const checkDarkMode = () => {
      setIsDarkMode(document.documentElement.classList.contains('dark'))
    }
    checkDarkMode()
    // Watch for class changes on html element
    const observer = new MutationObserver(checkDarkMode)
    observer.observe(document.documentElement, { attributes: true, attributeFilter: ['class'] })
    return () => observer.disconnect()
  }, [])
  const labelColor = isDarkMode ? '#f1f5f9' : '#1e293b'
  const referenceLineColor = isDarkMode ? '#cbd5e1' : '#94a3b8'
  const axisTitleColor = isDarkMode ? '#e2e8f0' : '#333'
  const axisSubtitleColor = isDarkMode ? '#cbd5e1' : '#666'
  const tickColor = isDarkMode ? '#94a3b8' : '#666'

  // Get break definition
  const breakDef = data?.breaks.find(b => b.id === selectedBreak)
  const { breakValues: filteredBreakValues, isFiltered: hasProvinceFilter } = useFilteredBreakValues()
  const breakValues = selectedBreak !== 'total' && breakDef ? filteredBreakValues : []
  const isBreakActive = selectedBreak !== 'total' && breakValues.length > 0
  const breakName = breakDef?.name || 'Total'
  const displayMap = (breakDef as { displayMap?: Record<string, string> })?.displayMap || {}

  // Always show labels - for regiondetailed we use short province names
  // which are less cluttered and will be grayed out when not hovered
  const showLabels = true

  // Calculate data points for each break value using pre-calculated PCA positions
  const breakDataPoints = useMemo(() => {
    if (!data || !isBreakActive) return []

    const points: DataPoint[] = []

    // Get pre-calculated UMAP positions for this break
    const positions = data?.scatterplotPositions?.[selectedBreak] || {}

    breakValues.forEach((value, index) => {
      // Use pre-calculated PCA position if available
      const pcaPosition = positions[value]

      // Normalize PCA values to 0-100 scale (same as tribe normalization)
      // X: PCA_2*-1 typically ranges from -2 to 2, map to 0-100
      // Y: PCA_1 typically ranges from -5.5 to 4.5, map to 0-100
      const xScore = pcaPosition ? normalizeX(pcaPosition.x) : 50
      const yScore = pcaPosition ? normalizeY(pcaPosition.y) : 50
      const sampleSize = pcaPosition?.sampleSize || 100

      // Use specific color palettes for different break types
      let color: string
      const demographicColors = (colorsConfig.scales as Record<string, Record<string, string>>).demographic || {}
      const provincezoneColors = (colorsConfig.scales as Record<string, Record<string, string>>).provincezone || {}

      if (selectedBreak === 'regiondetailed') {
        // Use detailed region colors (gradient shades within each province)
        color = REGION_DETAILED_COLORS[value] || TABLEAU_10[index % TABLEAU_10.length]
      } else if (selectedBreak === 'region' || selectedBreak === 'province') {
        // Direct province match for region/province break
        color = PROVINCE_COLORS[value] || TABLEAU_10[index % TABLEAU_10.length]
      } else if (selectedBreak === 'genderation') {
        // Use genderation colors (blue/pink gradients, lighter = younger)
        color = GENDERATION_COLORS[value] || TABLEAU_10[index % TABLEAU_10.length]
      } else if (selectedBreak === 'financial') {
        // Use financial situation colors (blue gradient, darker = wealthier)
        color = FINANCIAL_COLORS[value] || TABLEAU_10[index % TABLEAU_10.length]
      } else if (selectedBreak === 'party') {
        // Use political party colors (matching bar charts)
        color = PARTY_COLORS[value] || TABLEAU_10[index % TABLEAU_10.length]
      } else if (selectedBreak === 'ethnicity') {
        // Ethnicity colors
        color = ETHNICITY_COLORS[value] || TABLEAU_10[index % TABLEAU_10.length]
      } else if (selectedBreak === 'union') {
        // Union membership colors
        color = UNION_COLORS[value] || TABLEAU_10[index % TABLEAU_10.length]
      } else if (selectedBreak === 'tribes') {
        // Tribe colors from demographic config
        color = demographicColors[value] || TABLEAU_10[index % TABLEAU_10.length]
      } else if (selectedBreak === 'industry') {
        // Industry/Economic Sector colors
        color = INDUSTRY_COLORS[value] || TABLEAU_10[index % TABLEAU_10.length]
      } else if (selectedBreak === 'media') {
        // Media Sources colors
        color = MEDIA_COLORS[value] || TABLEAU_10[index % TABLEAU_10.length]
      } else if (selectedBreak === 'identity_verymuch') {
        // Identity colors
        color = IDENTITY_COLORS[value] || TABLEAU_10[index % TABLEAU_10.length]
      } else {
        // Fallback: check provincezone colors, demographic colors, then Tableau 10
        color = provincezoneColors[value] || demographicColors[value] || TABLEAU_10[index % TABLEAU_10.length]
      }

      let displayName = displayMap[value] || value
      let labelName: string | undefined
      let labelColor: string | undefined

      if (selectedBreak === 'regiondetailed') {
        // Extract province name for shorter label (e.g., "Ontario – Toronto" → "Ontario")
        const provincePart = value.split(' – ')[0]
        labelName = provincePart
        labelColor = PROVINCE_COLORS[provincePart] || color
      } else if (selectedBreak === 'industry') {
        // Use short names for industry sectors
        labelName = INDUSTRY_SHORT_NAMES[value] || value
      } else if (selectedBreak === 'financial') {
        // Use short names for financial situation
        labelName = FINANCIAL_SHORT_NAMES[value] || value
      } else if (selectedBreak === 'province' || selectedBreak === 'region') {
        // Use province abbreviations
        labelName = PROVINCE_SHORT_NAMES[value] || value
      } else if (selectedBreak === 'ethnicity') {
        // Use short ethnicity names
        labelName = ETHNICITY_SHORT_NAMES[value] || value
      } else if (selectedBreak === 'identity_verymuch') {
        // Use short identity names
        labelName = IDENTITY_SHORT_NAMES[value] || value
      }

      points.push({
        id: value,
        name: displayName,
        x: xScore,
        y: yScore,  // High Y = Mutualism (top), Low Y = Extraction (bottom)
        size: sampleSize,
        color,
        labelName,
        labelColor,
        showLabel: true,  // Default to showing label
      })
    })

    // For regiondetailed: control label visibility based on province filter
    if (selectedBreak === 'regiondetailed') {
      if (hasProvinceFilter) {
        // When a province filter is active, show ALL labels with SUB-REGION names
        // e.g., "Saskatchewan – Regina" becomes "Regina"
        points.forEach(p => {
          p.showLabel = true
          // Extract just the sub-region part for the label
          const parts = p.id.split(' – ')
          if (parts.length > 1) {
            p.labelName = parts[1]  // "Regina", "Saskatoon", "Rural", etc.
          }
        })
      } else {
        // When no filter, only show label on the LARGEST region per province (less clutter)
        // Labels show province name only
        // Group by province and find largest in each
        const provinceGroups: Record<string, typeof points[0][]> = {}
        points.forEach(p => {
          const province = p.labelName || p.id.split(' – ')[0]
          if (!provinceGroups[province]) provinceGroups[province] = []
          provinceGroups[province].push(p)
        })

        // Find largest region in each province and mark it
        const largestPerProvince = new Set<string>()
        Object.values(provinceGroups).forEach(group => {
          const largest = group.reduce((max, p) => p.size > max.size ? p : max, group[0])
          largestPerProvince.add(largest.id)
        })

        // Only show labels on the largest region per province
        points.forEach(p => {
          p.showLabel = largestPerProvince.has(p.id)
        })
      }
    }

    // For cluttered breaks (many points), show all labels - collision avoidance will handle positioning
    // (Previously limited to top 6 by size, but users want all labels visible)

    return points
  }, [data, selectedBreak, breakValues, displayMap, isBreakActive, hasProvinceFilter])

  // Reset placed labels on every render to prevent corruption from mouse moves
  resetPlacedLabels()

  // Calculate total population position when no break selected
  // Total Population is always at (0, 0) because it IS the mean/center by definition
  // (like Atlantic Canada model)
  const totalDataPoint = useMemo(() => {
    if (!data || isBreakActive) return null

    const totalPosition = data?.scatterplotPositions?.total as { x: number; y: number; sampleSize: number } | undefined
    const sampleSize = totalPosition?.sampleSize || 14078

    return [{
      id: 'total',
      name: 'Total Population',
      x: 0,  // Center horizontally - Total IS the mean
      y: 0,  // Center vertically - Total IS the mean
      size: sampleSize,
      color: '#4e79a7',
    }]
  }, [data, isBreakActive])

  // Determine what to show:
  // - If break is 'tribes', show TRIBE_POSITIONS (the 8 tribes from PCA analysis)
  // - If another break is selected, show breakDataPoints
  // - If 'total' (no break), show single total dot
  const isTribesBreak = selectedBreak === 'tribes'
  const chartData = isTribesBreak
    ? TRIBE_POSITIONS
    : (isBreakActive ? breakDataPoints : (totalDataPoint || []))

  // Calculate dynamic domains and centers based on data spread
  // Transform chart data to use sqrt(size) for area-proportional bubble sizing
  // Recharts ZAxis maps linearly to radius, so sqrt(size) makes area proportional to original size
  const chartDataWithAreaSize = useMemo(() => {
    // Calculate total for percentage computation
    const totalSize = chartData.reduce((sum, d) => sum + d.size, 0)

    const result = chartData.map(d => ({
      ...d,
      areaSize: Math.sqrt(d.size),  // Use sqrt for area-proportional sizing
      percentage: totalSize > 0 ? (d.size / totalSize) * 100 : 0,  // Calculate percentage for label
    }))
    // Update global positions for label collision detection
    // Note: These are data coordinates, not pixels, but work for relative positioning
    setPointPositions(result.map(d => ({ x: d.x, y: d.y })), 1000, 700)
    return result
  }, [chartData])

  const { xDomain, yDomain, xTicks, yTicks, centroidX, centroidY, sizeRange, breakTotal } = useMemo(() => {
    const sizes = chartDataWithAreaSize.map(d => d.size)
    const areaSizes = chartDataWithAreaSize.map(d => d.areaSize)

    // Calculate total for this break (sum of all segment sizes)
    const totalSize = sizes.reduce((sum, s) => sum + s, 0)

    // Calculate range for sqrt-transformed sizes - smallest gets 150, largest gets 800
    const sizeRangeCalc: [number, number] = areaSizes.length > 1
      ? [150, 800]
      : [400, 400] // Single point gets medium size

    // For Total view (single centered point), use fixed symmetric range (like Atlantic Canada)
    if (!isBreakActive && !isTribesBreak) {
      return {
        xDomain: [-3, 3] as [number, number],
        yDomain: [-3, 3] as [number, number],
        xTicks: [-3, -2, -1, 0, 1, 2, 3],
        yTicks: [-3, -2, -1, 0, 1, 2, 3],
        centroidX: 0,
        centroidY: 0,
        sizeRange: sizeRangeCalc,
        breakTotal: totalSize,
      }
    }

    // For other views: Dynamic domain based on data spread (like Atlantic Canada)
    const xValues = chartDataWithAreaSize.map(d => d.x)
    const yValues = chartDataWithAreaSize.map(d => d.y)

    const xMin = Math.min(...xValues)
    const xMax = Math.max(...xValues)
    const yMin = Math.min(...yValues)
    const yMax = Math.max(...yValues)

    // Calculate weighted centroid for grid line positioning (like Atlantic Canada)
    const sumSize = chartDataWithAreaSize.reduce((sum, p) => sum + p.size, 0)
    const centroidXCalc = chartDataWithAreaSize.reduce((sum, p) => sum + p.x * p.size, 0) / sumSize
    const centroidYCalc = chartDataWithAreaSize.reduce((sum, p) => sum + p.y * p.size, 0) / sumSize

    // Ensure minimum range for single-point scenarios
    const minRange = 1.5
    const xRange = Math.max(xMax - xMin, minRange)
    const yRange = Math.max(yMax - yMin, minRange)

    // Wider padding (30%) to show more context and reduce zoom
    // Also ensure 0 is always included so crosshairs are visible
    const xDomainMin = Math.min(xMin - xRange * 0.30, -0.1)
    const xDomainMax = Math.max(xMax + xRange * 0.30, 0.1)
    const yDomainMin = Math.min(yMin - yRange * 0.35, -0.1)
    const yDomainMax = Math.max(yMax + yRange * 0.30, 0.1)
    // Generate ticks that always include 0
    const generateTicks = (min: number, max: number): number[] => {
      const ticks: number[] = [0] // Always include 0
      const range = max - min
      const step = range < 1 ? 0.2 : range < 2 ? 0.5 : 1.0
      for (let t = -step; t >= min; t -= step) ticks.push(Math.round(t * 10) / 10)
      for (let t = step; t <= max; t += step) ticks.push(Math.round(t * 10) / 10)
      return ticks.sort((a, b) => a - b)
    }

    return {
      xDomain: [xDomainMin, xDomainMax] as [number, number],
      yDomain: [yDomainMin, yDomainMax] as [number, number],
      xTicks: generateTicks(xDomainMin, xDomainMax),
      yTicks: generateTicks(yDomainMin, yDomainMax),
      centroidX: centroidXCalc,
      centroidY: centroidYCalc,
      sizeRange: sizeRangeCalc,
      breakTotal: totalSize,
    }
  }, [chartDataWithAreaSize, isBreakActive, isTribesBreak])

  // Use consistent domain for all breaks (no zoom distortion)

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-slate-800 dark:text-white">
          Values Map
        </h1>
        <span className="text-sm text-slate-500 dark:text-slate-400">
          {isTribesBreak ? 'Thought Tribes' : (isBreakActive ? `${breakName} Value Positions` : 'Total Population')}
        </span>
      </div>

      <div className="bg-white dark:bg-slate-800 rounded-xl shadow-sm p-4">
        <div className="h-[700px] w-full max-w-[1000px] mx-auto">
          <ResponsiveContainer width="100%" height="100%">
            <ScatterChart
              margin={{ top: 40, right: 150, bottom: 60, left: 100 }}
              onMouseLeave={handleHoverLeave}
            >
              {/* Reference lines fixed at origin (0,0 = average Canadian) */}
              <ReferenceLine x={0} stroke={referenceLineColor} strokeDasharray="3 3" />
              <ReferenceLine y={0} stroke={referenceLineColor} strokeDasharray="3 3" />

              <XAxis
                type="number"
                dataKey="x"
                domain={xDomain}
                ticks={xTicks}
                name="Attitude Toward Governance"
                tickFormatter={(value) => Math.abs(value) < 0.05 ? '0.0' : value.toFixed(1)}
                tick={{ fontSize: 10, fill: tickColor }}
              >
                <Label
                  value="Attitude Toward Governance"
                  position="bottom"
                  offset={12}
                  style={{ fill: axisTitleColor, fontSize: 13, fontWeight: 600 }}
                />
                <Label
                  value="Populist Alienation ←――――――→ Trust in Rules/Process"
                  position="bottom"
                  offset={28}
                  style={{ fill: axisSubtitleColor, fontSize: 10 }}
                />
              </XAxis>
              <YAxis
                type="number"
                dataKey="y"
                domain={yDomain}
                ticks={yTicks}
                name="Attitude Toward Nature"
                tickFormatter={(value) => Math.abs(value) < 0.05 ? '0.0' : value.toFixed(1)}
                tick={{ fontSize: 10, fill: tickColor }}
                width={60}
              >
                <Label
                  value="Attitude Toward Nature"
                  angle={-90}
                  position="insideLeft"
                  offset={-10}
                  style={{ fill: axisTitleColor, fontSize: 13, fontWeight: 600, textAnchor: 'middle' }}
                />
                <Label
                  value="Humans First/Extraction ←――――――→ Mutualism/Co-dependence"
                  angle={-90}
                  position="insideLeft"
                  offset={8}
                  style={{ fill: axisSubtitleColor, fontSize: 10, textAnchor: 'middle' }}
                />
              </YAxis>
              <ZAxis
                type="number"
                dataKey="areaSize"
                range={sizeRange}
                name="Group Size"
              />
              {/* No Recharts Tooltip - using custom info box below chart */}

              {isTribesBreak ? (
                // Show tribes from PCA analysis
                <Scatter
                  name="Thought Tribes"
                  data={chartDataWithAreaSize}
                  isAnimationActive={false}
                  label={(props: CustomLabelProps & { index?: number }) => {
                    const dataPoint = props.index !== undefined ? chartDataWithAreaSize[props.index] : undefined
                    return (
                      <CustomScatterLabel
                        {...props}
                        fill={labelColor}
                        hasHoveredPoint={!!hoveredPoint}
                        isHighlighted={hoveredPoint?.id === props.name}
                        percentage={dataPoint?.percentage}
                      />
                    )
                  }}
                >
                  {chartDataWithAreaSize.map((entry) => (
                    <Cell
                      key={entry.id}
                      fill={getTribeColor(entry.id as TribeId)}
                      opacity={hoveredPoint ? (hoveredPoint.id === entry.id ? 1 : 0.4) : 1}
                      stroke="#fff"
                      strokeWidth={hoveredPoint?.id === entry.id ? 3 : 1}
                      style={{ cursor: 'pointer' }}
                      onMouseEnter={() => handleHoverEnter({ ...entry, color: getTribeColor(entry.id as TribeId) } as DataPoint)}
                      onMouseLeave={handleHoverLeave}
                    />
                  ))}
                </Scatter>
              ) : isBreakActive ? (
                // Show break values as data points
                <Scatter
                  name={breakName}
                  data={chartDataWithAreaSize}
                  isAnimationActive={false}
                  label={showLabels ? (props: CustomLabelProps & { index?: number }) => {
                    // Look up the actual data point to get showLabel, labelName, labelColor, percentage
                    const dataPoint = props.index !== undefined ? chartDataWithAreaSize[props.index] : undefined
                    if ((dataPoint as DataPoint)?.showLabel === false) return <></>
                    return (
                      <CustomScatterLabel
                        {...props}
                        labelName={(dataPoint as DataPoint)?.labelName}
                        labelColor={(dataPoint as DataPoint)?.labelColor}
                        showLabel={(dataPoint as DataPoint)?.showLabel}
                        fill={labelColor}
                        hasHoveredPoint={!!hoveredPoint}
                        isHighlighted={hoveredPoint?.id === dataPoint?.id || hoveredPoint?.name === props.name}
                        percentage={dataPoint?.percentage}
                      />
                    )
                  } : undefined}
                >
                  {chartDataWithAreaSize.map((entry) => (
                    <Cell
                      key={entry.id}
                      fill={(entry as DataPoint).color || '#4e79a7'}
                      opacity={hoveredPoint ? (hoveredPoint.id === entry.id ? 1 : 0.4) : 0.85}
                      stroke="#fff"
                      strokeWidth={hoveredPoint?.id === entry.id ? 3 : 1}
                      style={{ cursor: 'pointer' }}
                      onMouseEnter={() => handleHoverEnter(entry as DataPoint)}
                      onMouseLeave={handleHoverLeave}
                    />
                  ))}
                </Scatter>
              ) : (
                // Show total population - single dot
                <Scatter
                  name="Total Population"
                  data={chartDataWithAreaSize}
                  isAnimationActive={false}
                  label={(props: CustomLabelProps & { index?: number }) => {
                    const dataPoint = props.index !== undefined ? chartDataWithAreaSize[props.index] : undefined
                    return (
                      <CustomScatterLabel
                        {...props}
                        fill={labelColor}
                        hasHoveredPoint={!!hoveredPoint}
                        isHighlighted={hoveredPoint?.name === props.name}
                        percentage={dataPoint?.percentage}
                      />
                    )
                  }}
                >
                  {chartDataWithAreaSize.map((entry) => (
                    <Cell
                      key={entry.id}
                      fill={(entry as DataPoint).color || '#4e79a7'}
                      opacity={1}
                      stroke="#fff"
                      strokeWidth={2}
                      style={{ cursor: 'pointer' }}
                      onMouseEnter={() => handleHoverEnter(entry as DataPoint)}
                      onMouseLeave={handleHoverLeave}
                    />
                  ))}
                </Scatter>
              )}
            </ScatterChart>
          </ResponsiveContainer>
        </div>

        {/* Dynamic Hover Info Box */}
        {hoveredPoint ? (
          <div
            className="mt-3 px-4 py-3 rounded-lg border transition-all duration-200"
            style={{
              borderColor: (hoveredPoint as DataPoint).color || '#4e79a7',
              backgroundColor: `${(hoveredPoint as DataPoint).color || '#4e79a7'}08`
            }}
          >
            <div className="grid grid-cols-2 gap-x-6 gap-y-1 text-xs text-slate-600 dark:text-slate-300">
              <div>
                <span className="text-slate-400 dark:text-slate-500">Break:</span>{' '}
                <span className="font-medium">{isTribesBreak ? 'Thought Tribes' : isBreakActive ? breakName : 'Total Population'}</span>
              </div>
              <div>
                <span className="text-slate-400 dark:text-slate-500">Segment:</span>{' '}
                <span className="font-medium">{hoveredPoint.name}</span>
                <span className="text-slate-400 dark:text-slate-500 ml-2">
                  ({((hoveredPoint.size / breakTotal) * 100).toFixed(1)}% of {isBreakActive || isTribesBreak ? 'break' : 'total'})
                </span>
              </div>
              <div>
                <span className="text-slate-400 dark:text-slate-500">Nature:</span>{' '}
                <span className="font-medium">{
                  Math.abs(hoveredPoint.y - centroidY) < 0.1 ? 'Neutral' :
                  hoveredPoint.y > centroidY ? 'Mutualism/Co-dependence' : 'Humans First/Extraction'
                }</span>
              </div>
              <div>
                <span className="text-slate-400 dark:text-slate-500">Governance:</span>{' '}
                <span className="font-medium">{
                  Math.abs(hoveredPoint.x - centroidX) < 0.1 ? 'Neutral' :
                  hoveredPoint.x > centroidX ? 'Trust in Rules/Process' : 'Populist Alienation'
                }</span>
              </div>
            </div>
          </div>
        ) : (
          <div className="mt-3 px-4 py-3 rounded-lg border border-dashed border-slate-300 dark:border-slate-600">
            <p className="text-center text-slate-400 dark:text-slate-500 text-xs">
              Hover over data points to see details
            </p>
          </div>
        )}

        {/* Explanation */}
        <div className="mt-6 p-4 bg-slate-50 dark:bg-slate-700 rounded-lg">
          <h3 className="font-medium text-slate-800 dark:text-white mb-2">
            How to Read the Terrestrial Conservation Values Map
          </h3>
          <div className="text-sm text-slate-600 dark:text-slate-300 space-y-3">
            <p>
              This chart maps Canadian audiences and audience attributes across two core value dimensions
              that shape how people think about land, nature, and conservation.
            </p>

            <div>
              <p className="font-medium text-slate-700 dark:text-slate-200">Vertical axis — Relationship to the natural world</p>
              <ul className="ml-4 mt-1 space-y-1">
                <li><strong>Top (Mutualism):</strong> Nature is seen as something to live with and steward, emphasizing balance, limits, and long-term care.</li>
                <li><strong>Bottom (Humans First/Extraction):</strong> Nature is viewed primarily as a resource for economic growth, jobs, and material security.</li>
              </ul>
            </div>

            <div>
              <p className="font-medium text-slate-700 dark:text-slate-200">Horizontal axis — Trust in systems and institutions</p>
              <ul className="ml-4 mt-1 space-y-1">
                <li><strong>Left (Populist Alienation):</strong> Low trust in governments, corporations, and formal institutions; preference for individuals and personal relationships.</li>
                <li><strong>Right (Institutional Trust):</strong> Higher confidence in public institutions, experts, regulation, evidence, and democratic process.</li>
              </ul>
            </div>

            <p>
              Audiences and audience attributes are positioned based on where they fall on these two dimensions—reflecting
              both how they relate to the natural world and where they place their trust.
            </p>

            <p className="italic">
              In short, the map shows not just <em>who</em> supports or opposes conservation, but <em>why</em>—highlighting
              the values and trust dynamics that shape public opinion and inform effective communication and engagement strategies.
            </p>

            {isBreakActive && (
              <p className="text-slate-500 dark:text-slate-400 mt-2">
                Currently showing: <strong>{breakName}</strong> segments. Bubble size indicates sample size.
              </p>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}
